import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [workstationInfo, setWorkstationInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for stored workstation info on mount
    const secretKey = localStorage.getItem('secretKey') || localStorage.getItem('terminalSecretKey');
    const storedInfo = localStorage.getItem('workstationInfo') || localStorage.getItem('terminalInfo');

    if (secretKey && storedInfo && storedInfo !== 'undefined' && storedInfo !== 'null') {
      try {
        const parsed = JSON.parse(storedInfo);
        if (parsed && typeof parsed === 'object') {
          setWorkstationInfo(parsed);
          // Migrate old localStorage keys to new ones
          if (localStorage.getItem('terminalSecretKey')) {
            localStorage.setItem('secretKey', secretKey);
            localStorage.setItem('workstationInfo', storedInfo);
            localStorage.removeItem('terminalSecretKey');
            localStorage.removeItem('terminalInfo');
          }
        } else {
          throw new Error('Invalid workstation info structure');
        }
      } catch (error) {
        console.error('Failed to parse stored workstation info:', error);
        localStorage.removeItem('secretKey');
        localStorage.removeItem('workstationInfo');
        localStorage.removeItem('terminalSecretKey');
        localStorage.removeItem('terminalInfo');
      }
    }

    setLoading(false);
  }, []);

  const login = useCallback(async (secretKey) => {
    try {
      const response = await authAPI.login(secretKey);
      const info = response.data.terminal || response.data.location;

      if (!info) {
        return { success: false, error: 'Invalid response from server' };
      }

      localStorage.setItem('secretKey', secretKey);
      localStorage.setItem('workstationInfo', JSON.stringify(info));
      setWorkstationInfo(info);

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || error.response?.data?.error || 'Login failed'
      };
    }
  }, []);

  const logout = () => {
    authAPI.logout();
    setWorkstationInfo(null);
  };

  const value = {
    // Expose as both new and legacy names for gradual migration
    workstationInfo,
    terminalInfo: workstationInfo,
    loading,
    login,
    logout,
    isAuthenticated: !!workstationInfo
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
